import React, { useMemo } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    FormGroup,
    Radio,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import BackdropComponent from '../../../../../components/Backdrop/BackdropComponent';
import {
    TechnicalVisitFormDefinition,
    TechnicalVisitPayloadResponse,
    TechnicalVisitSection
} from '../types/technicalVisit.types';
import { useTechnicalVisitForm } from '../hooks/useTechnicalVisitForm';

const EMPTY_INITIAL_RESPONSES: any[] = [];

type TechnicalVisitFormProps = {
    formDefinition: TechnicalVisitFormDefinition;
    initialResponses?: any[];
    onBack: () => void;
    onSubmit: (respuestas: TechnicalVisitPayloadResponse[]) => Promise<void> | void;
    onDownloadPdf?: () => Promise<void> | void;
    isSubmitting?: boolean;
    isDownloadingPdf?: boolean;
    canDownloadPdf?: boolean;
    readOnly?: boolean;
    canEnableEdit?: boolean;
    onEnableEdit?: () => void;
};

const TechnicalVisitForm = ({
    formDefinition,
    initialResponses = EMPTY_INITIAL_RESPONSES,
    onBack,
    onSubmit,
    onDownloadPdf,
    isSubmitting = false,
    isDownloadingPdf = false,
    canDownloadPdf = false,
    readOnly = false,
    canEnableEdit = false,
    onEnableEdit
}: TechnicalVisitFormProps) => {
    const { t } = useTranslation('visitas');
    const {
        selectedAnswers,
        selectedRecomendaciones,
        payloadResponses,
        isValid,
        hasSeccionesIncumplidasSinRecomendacion,
        handleAnswerChange,
        handleRecomendacionToggle,
        getSelectedAnswer,
        getAnswerByDescription
    } = useTechnicalVisitForm(formDefinition, initialResponses);

    const getAnswerColumns = (section: TechnicalVisitSection) => {
        const uniqueAnswers = new Map<
            string,
            { key: string; descripcion: string; orden: number }
        >();

        section.preguntas.forEach((question) => {
            question.tiposRespuesta.forEach((answer) => {
                const normalizedDescription = answer.descripcion.trim().toUpperCase();

                if (!uniqueAnswers.has(normalizedDescription)) {
                    uniqueAnswers.set(normalizedDescription, {
                        key: normalizedDescription,
                        descripcion: answer.descripcion,
                        orden: answer.orden
                    });
                }
            });
        });

        return Array.from(uniqueAnswers.values()).sort((a, b) => a.orden - b.orden);
    };

    const tableColumns = (section: TechnicalVisitSection) => {
        const answerColumns = getAnswerColumns(section);
        const radioColumnWidth = answerColumns.length > 0 ? `${14 / answerColumns.length}%` : '5%';

        return [
            { key: 'nro', label: t('technicalVisit.columns.nro'), width: '11%', align: 'left' },
            {
                key: 'title',
                label: section.titulo || formDefinition.titulo,
                width: '75%',
                align: 'left'
            },
            ...answerColumns.map((column) => ({
                key: column.key,
                label: column.descripcion,
                width: radioColumnWidth,
                align: 'center'
            }))
        ];
    };

    return (
        <Box sx={{ backgroundColor: 'white', mt: 3, p: 3 }}>
            <BackdropComponent loading={isSubmitting || isDownloadingPdf} />
            <Stack spacing={3}>
                {formDefinition.secciones.map((section) => {
                    const answerColumns = getAnswerColumns(section);
                    const columns = tableColumns(section);

                    return (
                        <Box key={section.id}>
                            {formDefinition.secciones.length > 1 && (
                                <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
                                    {section.titulo}
                                </Typography>
                            )}

                            <TableContainer sx={{ border: '1px solid #E0E0E0' }}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {columns.map((column) => (
                                                <TableCell
                                                    key={column.key}
                                                    align={column.align as 'center' | 'left'}
                                                    sx={{
                                                        backgroundColor: '#34464D',
                                                        color: 'white',
                                                        fontWeight: 700,
                                                        fontSize: 12,
                                                        height: 40,
                                                        borderBottom: 'none',
                                                        py: 1,
                                                        width: column.width
                                                    }}
                                                >
                                                    {column.label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {section.preguntas
                                            .slice()
                                            .sort((a, b) => a.orden - b.orden)
                                            .map((question) => {
                                                const selectedAnswer = getSelectedAnswer(question);
                                                const shouldRequireRecommendation =
                                                    selectedAnswer?.requiereRecomendacion &&
                                                    question.recomendaciones.length > 0;
                                                const questionSelectedRecomendaciones =
                                                    selectedRecomendaciones[question.id] ?? [];

                                                return (
                                                    <React.Fragment key={question.id}>
                                                        <TableRow>
                                                            <TableCell
                                                                sx={{
                                                                    fontSize: 13,
                                                                    color: '#384A52',
                                                                    borderBottom:
                                                                        shouldRequireRecommendation
                                                                            ? 'none'
                                                                            : '1px solid #E0E0E0'
                                                                }}
                                                            >
                                                                {question.orden}
                                                            </TableCell>
                                                            <TableCell
                                                                sx={{
                                                                    fontSize: 13,
                                                                    color: '#384A52',
                                                                    borderBottom:
                                                                        shouldRequireRecommendation
                                                                            ? 'none'
                                                                            : '1px solid #E0E0E0'
                                                                }}
                                                            >
                                                                {question.texto}
                                                            </TableCell>
                                                            {answerColumns.map((column) => {
                                                                const answer =
                                                                    getAnswerByDescription(
                                                                        question,
                                                                        column.descripcion
                                                                    );

                                                                return (
                                                                    <TableCell
                                                                        key={column.key}
                                                                        align="center"
                                                                        sx={{
                                                                            borderBottom:
                                                                                shouldRequireRecommendation
                                                                                    ? 'none'
                                                                                    : '1px solid #E0E0E0'
                                                                        }}
                                                                    >
                                                                        {answer && (
                                                                            <Radio
                                                                                disabled={readOnly}
                                                                                checked={
                                                                                    selectedAnswers[
                                                                                        question.id
                                                                                    ] === answer.id
                                                                                }
                                                                                onChange={() =>
                                                                                    handleAnswerChange(
                                                                                        question,
                                                                                        answer
                                                                                    )
                                                                                }
                                                                                size="small"
                                                                                sx={{
                                                                                    color: '#384A52',
                                                                                    '&.Mui-checked':
                                                                                        {
                                                                                            color: '#384A52'
                                                                                        }
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </TableCell>
                                                                );
                                                            })}
                                                        </TableRow>

                                                        {shouldRequireRecommendation && (
                                                            <TableRow>
                                                                <TableCell
                                                                    colSpan={columns.length}
                                                                    sx={{
                                                                        background: '#FAFAFA',
                                                                        borderBottom:
                                                                            '1px solid #E0E0E0',
                                                                        px: 1,
                                                                        py: 1.5
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            color: '#1E272E',
                                                                            fontSize: 13,
                                                                            fontWeight: 700,
                                                                            mb: 1
                                                                        }}
                                                                    >
                                                                        {t(
                                                                            'technicalVisit.form.recommendations'
                                                                        )}
                                                                    </Typography>
                                                                    {question.recomendaciones
                                                                        .length === 0 ? (
                                                                        <Typography
                                                                            sx={{
                                                                                fontSize: 13,
                                                                                color: '#6B7280'
                                                                            }}
                                                                        >
                                                                            {t(
                                                                                'technicalVisit.form.noRecommendationsAvailable'
                                                                            )}
                                                                        </Typography>
                                                                    ) : (
                                                                        <FormGroup>
                                                                            {question.recomendaciones.map(
                                                                                (recomendacion) => (
                                                                                    <FormControlLabel
                                                                                        key={
                                                                                            recomendacion.id
                                                                                        }
                                                                                        disabled={
                                                                                            readOnly
                                                                                        }
                                                                                        control={
                                                                                            <Checkbox
                                                                                                size="small"
                                                                                                checked={questionSelectedRecomendaciones.includes(
                                                                                                    recomendacion.id
                                                                                                )}
                                                                                                onChange={(
                                                                                                    event
                                                                                                ) =>
                                                                                                    handleRecomendacionToggle(
                                                                                                        question.id,
                                                                                                        recomendacion.id,
                                                                                                        event
                                                                                                            .target
                                                                                                            .checked
                                                                                                    )
                                                                                                }
                                                                                                sx={{
                                                                                                    color: '#384A52',
                                                                                                    '&.Mui-checked':
                                                                                                        {
                                                                                                            color: '#384A52'
                                                                                                        }
                                                                                                }}
                                                                                            />
                                                                                        }
                                                                                        label={
                                                                                            <Typography
                                                                                                sx={{
                                                                                                    fontSize: 13
                                                                                                }}
                                                                                            >
                                                                                                {
                                                                                                    recomendacion.texto
                                                                                                }
                                                                                            </Typography>
                                                                                        }
                                                                                    />
                                                                                )
                                                                            )}
                                                                        </FormGroup>
                                                                    )}
                                                                </TableCell>
                                                            </TableRow>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    );
                })}
            </Stack>

            {!readOnly && hasSeccionesIncumplidasSinRecomendacion && (
                <Alert severity="warning" sx={{ mt: 3 }}>
                    {t('technicalVisit.form.missingRecommendationsWarning')}
                </Alert>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center" mt={3}>
                <Button variant="outlined" onClick={onBack}>
                    {t('common.goBack')}
                </Button>

                <Stack direction="row" spacing={2}>
                    {readOnly && canEnableEdit && (
                        <Button variant="outlined" onClick={onEnableEdit}>
                            {t('buttons.edit')}
                        </Button>
                    )}
                    {canDownloadPdf && (
                        <Button
                            variant="outlined"
                            onClick={onDownloadPdf}
                            disabled={isDownloadingPdf || isSubmitting}
                        >
                            {t('technicalVisit.form.downloadPdf')}
                        </Button>
                    )}
                    {!readOnly && (
                        <Button
                            variant="contained"
                            onClick={() => onSubmit(payloadResponses)}
                            disabled={!isValid || isSubmitting || isDownloadingPdf}
                        >
                            {t('buttons.save')}
                        </Button>
                    )}
                </Stack>
            </Stack>
        </Box>
    );
};

export default TechnicalVisitForm;
